<template>
  <div class="bonuses-grid" :class="{ mobile: isMobile }">
    <article
      v-for="bonus in extentedBonuses"
      :key="bonus.title"
      class="bonus-card"
      :data-t="`bonus-card-${bonus.type}-sg2a`"
      :class="[bonus.background, { link: bonus.link }]"
      @click="handleBonusClick(bonus)"
    >
      <img class="bonus-image" :src="bonus.icon" alt="icon" />
      <div class="bonus-content">
        <h4 class="bonus-title">
          {{ bonus.title }}
          <span v-if="bonus.additionalInfo?.percent">
            {{ `${bonus.additionalInfo.percent}%` }}
          </span>
          <StIcon
            v-if="bonus.link"
            class="chevron"
            name="chevron-right"
            :size="16"
          />
        </h4>
        <p class="bonus-text">{{ bonus.text }}</p>
      </div>
      <div v-if="bonus.checked" class="check-icon-wrapper">
        <StIcon name="check" :size="12" />
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import { useAllBonusesGrid } from './useAllBonusesGrid'
import type { ExtendedBonus } from './types'

const { isMobile } = usePlatform()

const { extentedBonuses } = useAllBonusesGrid()

function handleBonusClick(bonus: ExtendedBonus) {
  if (bonus.link) {
    navigateTo(bonus.link, { external: true })
  }
}
</script>

<style scoped>
.bonuses-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-200);
}

.bonus-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-050);
  padding-bottom: var(--spacing-025);
}

.bonus-title {
  display: inline-flex;
  gap: var(--spacing-050);
  align-items: center;

  margin: 0;

  font: var(--desktop-text-md-semibold);
}

.bonus-text {
  margin: 0;
  font: var(--desktop-text-md-medium);
  color: var(--text-secondary);
}

.bonus-image {
  width: 56px;
  height: 56px;
}

.bonus-general {
  overflow: hidden;
  display: flex;
  flex-direction: column;

  background-image: url('../../assets/bg-gray.jpg');
  background-size: cover;

  .bonus-general-image {
    position: absolute;
    right: 0;
    bottom: 0;

    width: 157px;
    height: auto;
  }

  .bonus-general-text {
    max-width: 240px;
  }
}

.check-icon-wrapper {
  position: absolute;
  top: var(--spacing-100);
  right: var(--spacing-100);

  display: flex;
  align-items: center;
  justify-content: center;

  width: 20px;
  height: 20px;
  padding: var(--spacing-050);

  background-color: var(--background-brand, #fe5e01);
  border-radius: var(--border-radius-full);
}

.chevron {
  transition: transform 0.2s;
}

.bonus-card {
  position: relative;

  display: flex;
  gap: var(--spacing-200);

  padding: var(--spacing-150) var(--spacing-200);

  background-size: cover;
  border-radius: var(--border-radius-150);

  &.blue {
    background: linear-gradient(95.31deg, #1b2b7d 0%, #111c50 100%);
  }

  &.green {
    background: linear-gradient(95.31deg, #075528 0%, #053319 100%);
  }

  &.bronze {
    background: linear-gradient(95.31deg, #792d01 0%, #471c01 100%);
  }

  &.red {
    background: linear-gradient(95.31deg, #721528 0%, #3d0b15 100%);
  }

  &.link {
    cursor: pointer;
  }

  &:hover {
    .chevron {
      transform: translateX(5px);
    }
  }
}

.mobile {
  &.bonuses-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-100);
    padding: var(--spacing-150) var(--spacing-200) var(--spacing-200);
  }

  .bonus-title {
    font: var(--mobile-text-semibold);
  }

  .bonus-text {
    font: var(--mobile-text-content-regular);
  }

  .bonus-image {
    width: 44px;
    height: 44px;
  }

  .bonus-card {
    gap: var(--spacing-150);
    min-height: 84px;
    padding: var(--spacing-125) var(--spacing-150);
  }

  .bonus-content {
    padding-top: var(--spacing-025);
    padding-bottom: 0;
  }

  .bonus-general {
    justify-content: center;
    min-height: 98px;

    .bonus-general-image {
      right: -16px;
      bottom: -10px;
    }
  }
}
</style>
